import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Member, Milestone, Resource, Team, TeamRole, Employee, Environment, EnvironmentCred, Project, TeamCard, MilestoneCard, ResourceCard, ProjectCard, Subtask, Task, TaskAttachment } from 'src/app/core/models/project-pilot.model';
import { Message, TableData } from '../models/domain';

@Injectable({
  providedIn: 'root'
})
export class ProjectPilotService {

  constructor(
    private httpClient: HttpClient
  ) { }

  //TEAM API'S
  public createTeam(team: Team): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/team`, team)
  }

  public getTeamsByPage(page: number, size: number, disabled?: boolean, needMembers?: boolean): Observable<TableData> {
    return this.httpClient.get<TableData>('/api/project-pilot/v1/teams/page', { params: { ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled != null && { disabled }), ...(needMembers && { needMembers }) } });
  }

  public getTeam(id: number): Observable<Team> {
    return this.httpClient.get<Team>(`/api/project-pilot/v1/team/${id}`);
  }

  public updateTeam(id: number, team: Team): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/team/${id}`, team);
  }

  public deleteTeams(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/team/${id}`);
  }

  public toggleTeamStatus(id: number,disabled: boolean): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/team/disable?id=${id}&disabled=${disabled}`, {});
  }

  public getTeams(disabled?: boolean): Observable<Team[]> {
    return this.httpClient.get<Team[]>(`/api/project-pilot/v1/teams`,{ params: { ...(disabled != null && { disabled }) } });
  }

  //TEAM ROLE API'S
  public createTeamRole(role: TeamRole): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/team-role`, role);
  }

  public getTeamRoles(page: number, size: number, disabled?: boolean,needMembers?: boolean): Observable<TableData> {
    return this.httpClient.get<TableData>('/api/project-pilot/v1/team-roles', { params: { ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled != null && { disabled }),...(needMembers && { needMembers })  } });
  }

  public getTeamRole(id: number): Observable<TeamRole> {
    return this.httpClient.get<TeamRole>(`/api/project-pilot/v1/team-role/${id}`);
  }

  public updateTeamRole(id: number, role: TeamRole): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/team-role/${id}`, role);
  }

  public deleteTeamRole(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/team-role/${id}`);
  }

  public toggleTeamRoleStatus(id: number, disabled?: boolean): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/team-role/disable?id=${id}&disabled=${disabled}`, {});
  }

  //MEMBER API'S
  public addMembers(teamId: number, employeeIds: number[]): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/team/member`, {}, { params: { teamId, ...(employeeIds.length ? { employeeIds: employeeIds.join(',') } : {}) } });
  }

  public getMembers(teamId: number, disabled?: boolean): Observable<Member[]> {
    return this.httpClient.get<Member[]>(`/api/project-pilot/v1/team/members`, { params: { teamId, ...(disabled && { disabled }) } });
  }

  public updateMember(id: number, teamRoleId: number): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/team/member/${id}?teamRoleId=${teamRoleId}`, {});
  }

  public deleteMembers(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/team/member/${id}`);
  }

  public toggleTeamMemberStatus(id: number, disabled?: boolean): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/team/member/status`, {}, { params: { id, disabled } });
  }

  // MILESTONE API'S
  public createMilestone(milestone: Milestone): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/milestone`, milestone);
  }

  public getMilestones(disabled?: boolean, projectId?: number, status?: string,
    priority?: string,
    startDate?: string,
    endDate?: string): Observable<Milestone[]> {
    return this.httpClient.get<Milestone[]>('/api/project-pilot/v1/milestones', { params: { ...(disabled != null && { disabled }), ...(projectId && { projectId }), ...(startDate && { startDate }), ...(endDate && { endDate }), ...(status && { status }), ...(priority && { priority }), } });
  }

  public getMilestonesByPage(page: number, size: number, disabled?: boolean, projectId?: number, status?: string,
    priority?: string,
    startDate?: string,
    endDate?: string): Observable<TableData> {
    return this.httpClient.get<TableData>('/api/project-pilot/v1/milestones/page', { params: { ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled != null && { disabled }), ...(projectId && { projectId }), ...(startDate && { startDate }), ...(endDate && { endDate }), ...(status && { status }), ...(priority && { priority }), } });
  }

  public getMilestone(id: number): Observable<Milestone> {
    return this.httpClient.get<Milestone>(`/api/project-pilot/v1/milestone/${id}`);
  }

  public updateMilestone(id: number, milestone: Milestone): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/milestone/${id}`, milestone);
  }

  public deleteMilestone(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/milestone/${id}`);
  }

  public toggleMilestoneStatus(milestone: Milestone): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/milestone/${milestone.id}`,milestone );
  }

  // RESURCE API'S
  public getMilestoneList(): Observable<Milestone[]> {
    return this.httpClient.get<Milestone[]>(`/api/project-pilot/v1/milestones`);
  }

  // RESOURCE API'S
  public createResource(resource: Resource): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/milestone/resource`, resource);
  }

  public getResources(milestoneId: number, disabled?: boolean): Observable<Resource[]> {
    return this.httpClient.get<Resource[]>(`/api/project-pilot/v1/milestone/resources`, { params: { milestoneId, ...(disabled && { disabled }) } });
  }

  public getResourcesByMilestone(milestoneId: number, page: number, size: number, disabled?: boolean): Observable<TableData> {
    return this.httpClient.get<TableData>(`/api/project-pilot/v1/milestone/${milestoneId}/resources`, { params: { ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled && { disabled }) } });
  }

  public fetchMilestones(disabled?: boolean): Observable<Milestone[]> {
    return this.httpClient.get<Milestone[]>(`/api/project-pilot/v1/milestones`, { params: { ...(disabled != null && { disabled }) } });
  }

  public getResource(id: number): Observable<Resource> {
    return this.httpClient.get<Resource>(`/api/project-pilot/v1/milestone/resource/${id}`);
  }

  public updateResource(id: number, resource: Resource): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/milestone/resource/${id}`, resource);
  }

  public deleteResource(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/milestone/resource/${id}`);
  }

  public toggleResourceStatus(ids: number[]): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/milestone/resources/disable?ids=${ids}`, {});
  }

  public upsertMilestoneFile(milestoneResourceId: number, file?: FormData): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/milestone/resource/upload?milestoneResourceId=${milestoneResourceId}`, file);
  }

  public getEmployees(): Observable<Employee[]> {
    return this.httpClient.get<Employee[]>(`/api/project-pilot/v1/team/member/profile`);
  }

  //PROJECT API'S
  public createProject(project: Project): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/project`, project)
  }

  public getProjectByPage(page: number, size: number, teamId?: number, status?: string, disabled?: boolean): Observable<TableData> {
    return this.httpClient.get<TableData>(`/api/project-pilot/v1/projects/page`, { params: { ...(page > -1 && { page }), ...(size > -1 && { size }), ...(teamId && { teamId }), ...(status && { status }), ...(disabled && { disabled }), } });
  }

  public getProjects(): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/project-pilot/v1/projects`);
  }

  public getProject(id: number): Observable<Project> {
    return this.httpClient.get<Project>(`/api/project-pilot/v1/project/${id}`);
  }

  public fetchProjects(disabled?: boolean): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/project-pilot/v1/projects`, { params: { ...(disabled != null && { disabled }) } });
  }

  public updateProject(id: number, project: Project): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/project/${id}`, project);
  }

  public deleteProject(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/project/${id}`);
  }

  public getStatuses(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/project/status`)
  }

  public getPriorities(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/project/priority`)
  }

  public getRiskLevels(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/project/risk-level`)
  }

  public getProjectTypes(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/project/type`)
  }

  public getCompletionQuality(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/project/completion-quality`)
  }

  public toggleProjectStatus(project: Project): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/project/${project.id}`, project);
  }

  //ENVIRONMENT API'S
  public createEnvironment(environment: Environment): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/environment`, environment);
  }

  public getEnvironments(projectId: number, name?: string, disabled?: boolean): Observable<Environment[]> {
    return this.httpClient.get<Environment[]>('/api/project-pilot/v1/environments', { params: { projectId, ...(name && { name }), ...(disabled && { disabled }) } });
  }

  public getEnvironment(id: number): Observable<Environment> {
    return this.httpClient.get<Environment>(`/api/project-pilot/v1/environment/${id}`);
  }

  public updateEnvironment(id: number, environment: Environment): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/environment/${id}`, environment);
  }

  public deleteEnvironment(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/environment/${id}`);
  }

  public getEnvironmentStatus(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/environment/env`)
  }

  //ENVIRONMENT CREDENTIAL API'S
  public createEnvironmentCredentail(environmentCred: EnvironmentCred): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/environment/credential`, environmentCred);
  }

  public getEnvironmentCredentails(environmentId: number, type?: string, disabled?: boolean): Observable<EnvironmentCred[]> {
    return this.httpClient.get<EnvironmentCred[]>('/api/project-pilot/v1/environment/credentials', { params: { environmentId, ...(type && { type }), ...(disabled && { disabled }) } });
  }

  public getEnvironmentCredentail(id: number): Observable<EnvironmentCred> {
    return this.httpClient.get<EnvironmentCred>(`/api/project-pilot/v1/environment/credential${id}`);
  }

  public updateEnvironmentCredentail(id: number, environmentCred: EnvironmentCred): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/environment/credential/${id}`, environmentCred);
  }

  public deleteEnvironmentCredentail(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/environment/credential/${id}`);
  }

  public getCredentialType(): Observable<string> {
    return this.httpClient.get<string>(`/api/project-pilot/v1/environment/credential-type`)
  }

  //PROJECT INSIGHT
  public getProjectInsight(count: number): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/project-pilot/v1/project/insights`, { params: { count, } });
  }

  // TASK API'S
  public createTask(task: Task): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/task`, task);
  }

  public getTasks(projectId?: number, status?: string, priority?: string, startDate?: string, endDate?: string): Observable<Task[]> {
    return this.httpClient.get<Task[]>('/api/project-pilot/v1/tasks', { params: { ...(projectId && { projectId }), ...(startDate && { startDate }), ...(endDate && { endDate }), ...(status && { status }), ...(priority && { priority }), } });
  }

  public getTasksByPage(page: number, size: number, disabled?: boolean, projectId?: number, status?: string, priority?: string, startDate?: string, endDate?: string): Observable<TableData> {
    return this.httpClient.get<TableData>('getTasks', { params: { ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled && { disabled }), ...(projectId && { projectId }), ...(startDate && { startDate }), ...(endDate && { endDate }), ...(status && { status }), ...(priority && { priority }), } });
  }

  public getTask(id: number): Observable<Task> {
    return this.httpClient.get<Task>(`/api/project-pilot/v1/task/${id}`);
  }

  public updateTask(id: number, task: Task): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/task/${id}`, task);
  }

  public deleteTasks(ids: number[]): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/task?ids=${ids}`);
  }

  public upsertTaskStatus(id: number, status: string): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/task`, {}, { params: { id, status } });
  }

  public toggleTaskStatus(ids: number[]): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/tasks/disable?ids=${ids.join(',')}`, {});
  }

  //ATTACHMENT API'S
  public createTaskAttachment(attachment: TaskAttachment): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/task-attachment`, attachment);
  }

  public getTaskAttachments(taskId: number, page: number, size: number, disabled?: boolean): Observable<TableData> {
    return this.httpClient.get<TableData>('/api/project-pilot/v1/task-attachments', { params: { taskId, ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled && { disabled }) } });
  }

  public getTaskAttachment(id: number): Observable<TaskAttachment> {
    return this.httpClient.get<TaskAttachment>(`/api/project-pilot/v1/task-attachment/${id}`);
  }

  public uploadTaskAttachment(taskAttachmentId: number, file: FormData): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/task-attachment/upload?taskAttachmentId=${taskAttachmentId}`, file);
  }

  // SUBTASK API'S
  public createSubtask(subtask: Subtask): Observable<Message> {
    return this.httpClient.post<Message>(`/api/project-pilot/v1/subtask`, subtask);
  }

  public getSubtasks(taskId: number, page: number, size: number, disabled?: boolean, status?: string, priority?: string): Observable<TableData> {
    return this.httpClient.get<TableData>('/api/project-pilot/v1/subtasks', {
      params: { taskId, ...(page > -1 && { page }), ...(size > -1 && { size }), ...(disabled && { disabled }), ...(status && { status }), ...(priority && { priority }) }
    });
  }

  public getSubtask(id: number): Observable<Subtask> {
    return this.httpClient.get<Subtask>(`/api/project-pilot/v1/subtask/${id}`);
  }

  public updateSubtask(id: number, subtask: Subtask): Observable<Message> {
    return this.httpClient.put<Message>(`/api/project-pilot/v1/subtask/${id}`, subtask);
  }

  public deleteSubtask(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/project-pilot/v1/subtask/${id}`);
  }

  public toggleSubtaskStatus(ids: number[]): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/project-pilot/v1/subtasks/toggle-status`, { ids });
  }

  //TASK DEFALT API'S
  public getTaskStatuses(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/project-pilot/v1/task/task-status`);
  }

  public getTaskPriorities(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/project-pilot/v1/task/task-priority`);
  }

  //PROJECT INSIGHT
  public getProjectInsights(count?: number, status?: string, priority?: string): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/project-pilot/v1/project/insights`, { params: { ...(count && { count }), ...(status && { status }), ...(priority && { priority }) } });
  }

  public getProjectCard(projectId: number): Observable<ProjectCard> {
    return this.httpClient.get<ProjectCard>(`/api/project-pilot/v1/project/card-data`, { params: { projectId } });
  }

  //DEFAULT API'S
  public getPrivileges(): any[] {
    return [
      { id: 'VIEW', label: "Viewer" },
      { id: 'EDIT', label: "Editor" },
      { id: 'DELETE', label: "Owner" }
    ];
  }

  //Insights API's
  public getTeamInsights(count?: number, needMembers?: boolean): Observable<Team[]> {
    return this.httpClient.get<Team[]>(`/api/project-pilot/v1/team/insights`, { params: { ...(count && { count }), ...(needMembers && { needMembers }) } });
  }

  public getTeamCard(teamId: number): Observable<TeamCard> {
    return this.httpClient.get<TeamCard>(`/api/project-pilot/v1/team/card-data`, { params: { teamId } });
  }

  //MilestoneInsight API'S
  public getMilestoneInsights(count?: number): Observable<Milestone[]> {
    return this.httpClient.get<Milestone[]>(`/api/project-pilot/v1/milestone/insights`, { params: { ...(count && { count }) } });
  }

  public getMilestoneCard(teamId: number): Observable<MilestoneCard> {
    return this.httpClient.get<MilestoneCard>(`/api/project-pilot/v1/team/card-data`, { params: { teamId } });
  }

  //ResourceInsight API'S
  public getResourceInsights(teamId: number, count?: number): Observable<Member[]> {
    return this.httpClient.get<Member[]>(`/api/project-pilot/v1/team/member/insights`, { params: { ...(teamId && { teamId }), ...(count && { count }) } });
  }

  public getResourceCard(teamMemberId: number): Observable<ResourceCard> {
    return this.httpClient.get<ResourceCard>(`/api/project-pilot/v1/team/member/card-data`, { params: { teamMemberId } });
  }
}
